import React, { useEffect, useState } from 'react';
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import CreateNewFolderOutlinedIcon from '@material-ui/icons/CreateNewFolderOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FolderIcon from '@material-ui/icons/Folder';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { DefaultFolderNode, FolderStructure } from '../../api-client/autogenerated';
import {
  DESIGN_PATH_CHARACTER_LIMIT_FOLDER,
  FOLDER_ICON_COLOR,
  PROJECT_NAME_PLACEHOLDER,
} from '../../scripts/constants';
import { generateUniqueId } from '../../scripts/utils';
import { CancelButton, SubmitButton } from '../custom-components/CustomButtons';
import CircularLoader from '../loader/CircularLoader';

/** How deeply folders may nest, matching MAX_DEFAULT_FOLDER_DEPTH on the API. */
const MAX_FOLDER_DEPTH = 10;

/** Longest a structure's own name may be, matching MAX_FOLDER_STRUCTURE_NAME_LENGTH on the API. */
const MAX_STRUCTURE_NAME_LENGTH = 100;

/** Folder names CENTERLINE Drive accepts, matching the API's folder path validation. */
// eslint-disable-next-line no-useless-escape
const VALID_FOLDER_NAME_REGEX = /^[A-Za-z0-9-_\s~`\[\]!@#$%^&()=,;.]+$/;

/** A folder while it is being edited. `id` is local only -- it keeps React keys stable. */
type EditableFolder = {
  id: string;
  name: string;
  children: EditableFolder[];
};

const toEditableFolders = (folders: DefaultFolderNode[]): EditableFolder[] =>
  folders.map((folder) => ({
    id: generateUniqueId(),
    name: folder.name,
    children: toEditableFolders(folder.children || []),
  }));

const toDefaultFolderNodes = (folders: EditableFolder[]): DefaultFolderNode[] =>
  folders.map((folder) => ({
    name: folder.name.trim(),
    children: toDefaultFolderNodes(folder.children),
  }));

/** Replaces the folder with `id` using `update`, or removes it when `update` returns null. */
const mapFolder = (
  folders: EditableFolder[],
  id: string,
  update: (folder: EditableFolder) => EditableFolder | null,
): EditableFolder[] =>
  folders.flatMap((folder) => {
    if (folder.id === id) {
      const updated = update(folder);
      return updated ? [updated] : [];
    }
    return [{ ...folder, children: mapFolder(folder.children, id, update) }];
  });

/**
 * First problem that would stop the folders from saving, or undefined when they are valid. Mirrors
 * the API's validation so the admin sees the problem before submitting.
 */
const findFolderError = (folders: EditableFolder[], prefix = '/'): string | undefined => {
  const namesSeen = new Set<string>();
  for (const folder of folders) {
    const name = folder.name.trim();
    if (!name) return 'Folder names cannot be empty.';
    if (!VALID_FOLDER_NAME_REGEX.test(name)) return `"${name}" is not a valid folder name.`;
    if (namesSeen.has(name)) {
      return `Two folders in ${prefix} are both named "${name}".`;
    }
    namesSeen.add(name);

    const fullKey = `${prefix}${name}/`;
    if (fullKey.length > DESIGN_PATH_CHARACTER_LIMIT_FOLDER) {
      return `The path ${fullKey} exceeds the character limit of ${DESIGN_PATH_CHARACTER_LIMIT_FOLDER}.`;
    }

    const childError = findFolderError(folder.children, fullKey);
    if (childError) return childError;
  }
  return undefined;
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    detailFields: {
      display: 'flex',
      flexWrap: 'wrap',
      marginBottom: 16,
    },
    folderRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 0px',
    },
    folderIcon: {
      color: FOLDER_ICON_COLOR,
      marginRight: 8,
    },
    subfolderIcon: {
      color: '#7A797A',
      marginRight: 4,
    },
    // The project is the root of every project's CENTERLINE Drive, so it is shown but not editable.
    projectRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 0px',
    },
    projectName: {
      fontStyle: 'italic',
      color: '#7A797A',
    },
    nameField: {
      maxWidth: 320,
      flexGrow: 1,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 16,
    },
    error: {
      color: '#F1685E',
      marginTop: 8,
    },
  }),
);

export type FolderStructureDraft = {
  name: string;
  description: string;
  folders: DefaultFolderNode[];
};

type FolderStructureEditorProps = {
  /** The structure being edited, or undefined when creating a new one. */
  existingStructure?: FolderStructure;
  /** Structures a new one can be cloned from. Ignored when editing an existing structure. */
  cloneOptions?: FolderStructure[];
  /** Reports the structure being cloned from, so the caller can title the editor. */
  onCloneSourceChange?: (cloneSource?: FolderStructure) => void;
  onSave: (draft: FolderStructureDraft) => Promise<void>;
  onCancel: () => void;
};

/**
 * Builds one default CENTERLINE Drive folder structure: its name, an optional description, and the
 * folders it creates. The folders are saved as a whole, so adds, renames, deletes and nesting all
 * go up in one write.
 */
export default function FolderStructureEditor(props: FolderStructureEditorProps) {
  const { existingStructure, cloneOptions, onCloneSourceChange, onSave, onCancel } = props;
  const classes = useStyles();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [folders, setFolders] = useState<EditableFolder[]>([]);
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isCloneable = !existingStructure && !!cloneOptions?.length;

  /**
   * Cloning copies the folders only. The name and description stay blank, since a copy needs its
   * own -- and names have to be unique within the subscriber anyway.
   */
  const handleCloneSourceChange = (structureId: string) => {
    setCloneSourceId(structureId);
    const cloneSource = cloneOptions?.find((structure) => structure.id === structureId);
    setFolders(toEditableFolders(cloneSource?.folders || []));
    onCloneSourceChange?.(cloneSource);
  };

  useEffect(() => {
    setName(existingStructure?.name || '');
    setDescription(existingStructure?.description || '');
    setFolders(toEditableFolders(existingStructure?.folders || []));
  }, [existingStructure]);

  const getValidationError = () => {
    if (!name.trim()) return 'Give the folder structure a name.';
    if (name.trim().length > MAX_STRUCTURE_NAME_LENGTH) {
      return `The name cannot be longer than ${MAX_STRUCTURE_NAME_LENGTH} characters.`;
    }
    return findFolderError(folders);
  };
  const validationError = getValidationError();

  const addFolder = (parentId?: string) => {
    const newFolder: EditableFolder = { id: generateUniqueId(), name: '', children: [] };
    if (!parentId) {
      setFolders((current) => [...current, newFolder]);
      return;
    }
    setFolders((current) =>
      mapFolder(current, parentId, (folder) => ({
        ...folder,
        children: [...folder.children, newFolder],
      })),
    );
  };

  const renameFolder = (id: string, folderName: string) => {
    setFolders((current) => mapFolder(current, id, (folder) => ({ ...folder, name: folderName })));
  };

  const deleteFolder = (id: string) => {
    setFolders((current) => mapFolder(current, id, () => null));
  };

  const handleSave = async () => {
    if (validationError) return;
    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        folders: toDefaultFolderNodes(folders),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderFolders = (level: EditableFolder[], depth: number) =>
    level.map((folder) => (
      <div key={folder.id}>
        {/* Indented one extra level: everything here sits inside the project's root folder. */}
        <div className={classes.folderRow} style={{ marginLeft: (depth + 1) * 32 }}>
          {depth > 0 ? <SubdirectoryArrowRightIcon className={classes.subfolderIcon} /> : null}
          <FolderIcon className={classes.folderIcon} />
          <TextField
            variant="outlined"
            size="small"
            className={classes.nameField}
            placeholder="Folder Name"
            value={folder.name}
            inputProps={{ 'aria-label': 'Folder Name' }}
            onChange={(e) => renameFolder(folder.id, e.target.value)}
          />
          <Tooltip
            title={
              depth + 1 >= MAX_FOLDER_DEPTH
                ? `Folders cannot nest more than ${MAX_FOLDER_DEPTH} levels deep`
                : 'Add Subfolder'
            }
          >
            <span>
              <IconButton
                aria-label={`Add subfolder to ${folder.name || 'new folder'}`}
                disabled={depth + 1 >= MAX_FOLDER_DEPTH}
                onClick={() => addFolder(folder.id)}
              >
                <CreateNewFolderOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              folder.children.length
                ? 'Delete this folder and everything inside it'
                : 'Delete Folder'
            }
          >
            <IconButton
              aria-label={`Delete ${folder.name || 'new folder'}`}
              onClick={() => deleteFolder(folder.id)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>
        {renderFolders(folder.children, depth + 1)}
      </div>
    ));

  return (
    <div className={classes.root}>
      {isCloneable ? (
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
          <Typography variant="body1">Clone Existing Structure?</Typography>
          <Select
            variant="outlined"
            margin="dense"
            displayEmpty
            inputProps={{ 'aria-label': 'Clone Existing Structure' }}
            value={cloneSourceId}
            onChange={(event) => handleCloneSourceChange(event.target.value as string)}
            style={{ width: 300 }}
          >
            <MenuItem value="">None</MenuItem>
            {cloneOptions!.map((structure) => (
              <MenuItem key={structure.id} value={structure.id}>
                {structure.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      ) : null}
      <div className={classes.detailFields}>
        <div style={{ display: 'flex', flexDirection: 'column', marginRight: 24 }}>
          <Typography variant="body1">
            Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            style={{ width: 300 }}
            placeholder="Name"
            inputProps={{ 'aria-label': 'Folder Structure Name' }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1">Description</Typography>
          <TextField
            variant="outlined"
            size="small"
            style={{ width: 420 }}
            placeholder="Description"
            inputProps={{ 'aria-label': 'Folder Structure Description' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      {/* The project itself is the root of its CENTERLINE Drive, so the structure hangs off it. */}
      <div className={classes.projectRow}>
        <FolderIcon className={classes.folderIcon} />
        <Typography variant="body1" className={classes.projectName}>
          {PROJECT_NAME_PLACEHOLDER}
        </Typography>
      </div>
      {folders.length === 0 ? (
        <Typography
          variant="body1"
          style={{ fontStyle: 'italic', marginBottom: 8, marginLeft: 32 }}
        >
          No folders yet.
        </Typography>
      ) : (
        renderFolders(folders, 0)
      )}
      <div>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          style={{ marginTop: 8 }}
          onClick={() => addFolder()}
        >
          Add Root Folder
        </Button>
      </div>

      {validationError ? (
        <Typography variant="body1" className={classes.error}>
          {validationError}
        </Typography>
      ) : null}
      <div className={classes.actions}>
        {isSaving ? (
          <CircularLoader style={{ justifyContent: 'flex-start' }} />
        ) : (
          <>
            <SubmitButton
              onClick={handleSave}
              disabled={!!validationError}
              variant="contained"
              color="primary"
            >
              {existingStructure ? 'Save Folder Structure' : 'Create Folder Structure'}
            </SubmitButton>
            <CancelButton onClick={onCancel} style={{ marginLeft: 16 }}>
              Cancel
            </CancelButton>
          </>
        )}
      </div>
    </div>
  );
}
